import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Loader2, MailX, CheckCircle2, AlertTriangle, ArrowLeft, RotateCcw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Unsubscribe() {
  const [params] = useSearchParams();
  const v = params.get("v");
  const e = params.get("e");
  const t = params.get("t");

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState(null); // { venue_name, email, unsubscribed }

  useEffect(() => {
    if (!v || !e || !t) {
      setError("This unsubscribe link is incomplete. Please use the link from the email you received.");
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/unsubscribe/preview`, { params: { v, e, t } });
        setInfo(data);
      } catch (err) {
        setError(err?.response?.data?.detail || "Couldn't load your unsubscribe details.");
      } finally {
        setLoading(false);
      }
    })();
  }, [v, e, t]);

  const doUnsub = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/unsubscribe/confirm`, { v, e, t });
      setInfo((prev) => ({ ...(prev || {}), ...data }));
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Try again in a minute.");
    } finally {
      setBusy(false);
    }
  };

  const doResub = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/unsubscribe/resubscribe`, { v, e, t });
      setInfo((prev) => ({ ...(prev || {}), ...data }));
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Try again in a minute.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="unsubscribe-page"
      className="min-h-screen bg-black text-[#F5F5F0] grain flex items-center justify-center px-6 py-16"
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#D9A6F5]">
            <MailX size={14} /> Email preferences
          </div>
        </div>

        <div className="border border-white/10 bg-[#0A0A0A] p-8">
          {loading ? (
            <div className="flex flex-col items-center gap-4 py-8">
              <Loader2 size={22} className="animate-spin text-[#D9A6F5]" />
              <div className="text-xs uppercase tracking-[0.28em] text-[#A3A3A3]">
                Loading…
              </div>
            </div>
          ) : error ? (
            <div data-testid="unsub-error" className="text-center">
              <AlertTriangle size={32} className="text-red-400 mx-auto mb-3" />
              <div className="text-[10px] uppercase tracking-[0.28em] text-red-400 mb-3">
                Link not valid
              </div>
              <h1 className="text-2xl md:text-3xl font-black tracking-tighter mb-4">
                Hmm, that link didn&apos;t work.
              </h1>
              <p className="text-sm text-[#A3A3A3] leading-relaxed">{error}</p>
            </div>
          ) : info?.unsubscribed ? (
            <div data-testid="unsub-confirmed" className="text-center">
              <CheckCircle2 size={32} className="text-[#D9A6F5] mx-auto mb-3" />
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#D9A6F5] mb-3">
                You&apos;re unsubscribed
              </div>
              <h1 className="text-2xl md:text-3xl font-black tracking-tighter mb-4 leading-tight">
                No more emails from{" "}
                <span className="text-[#D9A6F5]">{info.venue_name}</span>.
              </h1>
              <p className="text-sm text-[#A3A3A3] leading-relaxed">
                <span className="text-[#F5F5F0] font-mono text-xs">{info.email}</span> won&apos;t
                receive further marketing emails from {info.venue_name}. You may still get
                emails for tickets or memberships you purchase (those are required for the
                transaction).
              </p>

              <button
                type="button"
                onClick={doResub}
                disabled={busy}
                data-testid="unsub-resub"
                className="mt-8 inline-flex items-center gap-2 border border-white/20 hover:border-[#D9A6F5] hover:text-[#D9A6F5] text-[#F5F5F0] px-5 py-3 text-[10px] uppercase tracking-[0.22em] font-bold disabled:opacity-40 transition-colors"
              >
                {busy ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
                Undo — subscribe again
              </button>
            </div>
          ) : info ? (
            <div data-testid="unsub-form" className="text-center">
              <MailX size={30} className="text-[#D9A6F5] mx-auto mb-3" />
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#D9A6F5] mb-3">
                Confirm unsubscribe
              </div>
              <h1 className="text-2xl md:text-3xl font-black tracking-tighter mb-4 leading-tight">
                Stop hearing from{" "}
                <span className="text-[#D9A6F5]">{info.venue_name}</span>?
              </h1>
              <p className="text-sm text-[#A3A3A3] leading-relaxed">
                We&apos;ll remove{" "}
                <span className="text-[#F5F5F0] font-mono text-xs">{info.email}</span> from{" "}
                {info.venue_name}&apos;s marketing list. You&apos;ll still get transactional
                emails you actively opt in to (tickets, memberships).
              </p>
              <button
                type="button"
                onClick={doUnsub}
                disabled={busy}
                data-testid="unsub-confirm"
                className="mt-8 inline-flex items-center gap-2 bg-[#D9A6F5] text-black hover:bg-[#E8C8FF] disabled:opacity-40 px-6 py-3 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
              >
                {busy ? <Loader2 size={13} className="animate-spin" /> : <MailX size={13} />}
                Unsubscribe
              </button>
              <div className="mt-4 text-[10px] uppercase tracking-[0.22em] text-[#A3A3A3]">
                Changed your mind?{" "}
                <a href="/" className="text-[#D9A6F5] hover:underline">Head back to Swingle</a>
              </div>
            </div>
          ) : null}
        </div>

        <div className="mt-6 text-center">
          <a
            href="/"
            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.28em] text-[#A3A3A3] hover:text-[#F5F5F0] transition-colors"
          >
            <ArrowLeft size={12} /> Back to Swingle
          </a>
        </div>
      </div>
    </div>
  );
}
